<template>
  <div id="page-add-product">
    <vs-card id="edit-product-container" class="vs-con-loading__container">
      <!-- Form -->
      <div class="vx-row px-5 pt-5">
        <!-- Col - 1 -->
        <div class="vx-col w-full md:w-1/2 px-8">
          <!-- Category Dropdown -->
          <div class="vx-row mb-2">
            <label for="">Category <span class="text-danger">*</span></label>
            <select-2
              autocomplete
              class="w-full"
              :value.sync="form.categoryId"
              @input="(item) => (form.categoryId = item && item.value)"
              name="Category"
              disabled
              v-validate="'required'"
              placeholder="Select Category"
              :params="{ is_global: user.roleId === DEMORETAILER ? null : 'global', is_status: true }"
              action="product/getAllProductCategories"
            />
            <span class="text-danger text-sm" v-show="errors.has('Category')">{{ errors.first('Category') }}</span>
          </div>

          <!-- Bussiness Dropdown -->
          <div class="vx-row mb-2" v-if="!isCustomCategory">
            <label for="">Manufacturer/Supplier Name <span class="text-danger">*</span></label>
            <select-2
              class="w-full"
              :value="form.supplierId"
              @input="
                (item) => {
                  this.form.supplierId = item && item.value
                  this.form.supplier_type = item && (item.type === 'user' ? 'user' : 'supplier')
                }
              "
              name="Business name"
              v-validate="'required'"
              placeholder="Select Business Type"
              autocomplete
              :extra="extraSupplierOptions"
              action="supplier/getSupplierOption"
            />
            <span class="text-danger text-sm" v-show="errors.has('Business name')">{{ errors.first('Business name') }}</span>
          </div>

          <!-- Product Name -->
          <div class="vx-row mb-2">
            <label for="">Item Name <span class="text-danger">*</span></label>
            <vs-input icon="icon icon-list" v-model="form.product_name" :value="form.product_name" icon-pack="feather" class="w-full" name="Item Name" v-validate="'required'" />
            <span class="text-danger text-sm" v-show="errors.has('Item Name')">{{ errors.first('Item Name') }}</span>
          </div>

          <!-- Tiered Pricing Group -->
          <div class="vx-row mb-2" v-if="!isCustomCategory">
            <label for="">Tiered Pricing Group</label>
            <select-2
              autocomplete
              class="w-full"
              :value.sync="form.tieredPricingGroupId"
              @input="(item) => (form.tieredPricingGroupId = item && item.value)"
              name="Tiered Pricing Group"
              placeholder="Select Tiered Pricing Group"
              :options="tieredPricingOptions"
            />
          </div>

          <!-- Sales Price -->
          <div class="vx-row mb-2">
            <vx-tooltip class="w-full" position="left" text="This is the sale price per unit. Example: If you have 10 pounds for sale at $2000/lbs, then the amount you type here is 2000.">
              <vs-input
                icon="icon icon-list"
                type="number"
                step="0.01"
                v-model="form.sales_price"
                @change="calculateSalePrice"
                icon-pack="feather"
                class="w-full"
                label="Sales Price"
                name="Sales Price"
                v-validate="'required'"
              />
              <span class="text-danger text-sm" v-show="errors.has('Sales Price')">{{ errors.first('Sales Price') }}</span>
            </vx-tooltip>
          </div>
        </div>

        <!-- col - 2 -->
        <div class="vx-col w-full md:w-1/2 px-8">
          <!-- weight type -->
          <div class="vx-row mb-2" v-if="form.categoryId !== categories.PLANT && form.categoryId !== categories.MATURE && !isCustomCategory">
            <label for="">Weight Type <span class="text-danger">*</span></label>
            <select-2
              autocomplete
              class="w-full"
              :value="form.weight_type"
              @input="(item) => (form.weight_type = item && item.value)"
              :disabled="weightTypeDisabled"
              v-validate="'required'"
              name="Weight Type"
              placeholder="Select Weight Type"
              :options="weightTypeOptions"
            />
            <span class="text-danger text-sm" v-show="errors.has('Weight Type')">{{ errors.first('Weight Type') }}</span>
          </div>

          <!-- weight per package -->
          <template v-if="[categories.FLOWER, categories.SHAKE].includes(form.categoryId) && !isCustomCategory">
            <div class="vx-row mb-2">
              <vx-tooltip class="w-full" position="left" text="To edit the quantity of this product, please use the update quantity feature under action in the product list.">
                <label for="">Weight Per Package <span class="text-danger">*</span></label>
                <vs-input
                  icon="icon icon-list"
                  icon-pack="feather"
                  step="0.001"
                  type="number"
                  :value="mathRound(form.quantity || 0, 3)"
                  @change="(e) => (form.quantity = mathRound(e.target.value || 0, 3))"
                  class="w-full"
                  name="Weight Per Package"
                  v-validate="'required|min_value:0.001'"
                  disabled
                />
                <span class="text-danger text-sm" v-show="errors.has('Weight Per Package')">{{ errors.first('Weight Per Package') }}</span>
              </vx-tooltip>
            </div>
          </template>
          <template v-else-if="form.categoryId === categories.PLANT || form.categoryId === categories.MATURE">
            <div class="vx-row mb-2">
              <vs-input icon="icon icon-list" disabled icon-pack="feather" class="w-full" label="Quantity" v-model="form.quantity" name="Quantity" v-validate="'required'" />
              <span class="text-danger text-sm" v-show="errors.has('Quantity')">{{ errors.first('Quantity') }}</span>
            </div>
          </template>
          <template v-else-if="[categories.ANCILLARY, categories.OTHER].includes(form.categoryId)">
            <div class="vx-row mb-2">
              <vx-tooltip class="w-full" position="left" text="To edit the quantity of this product, please use the update quantity feature under action in the product list.">
                <vs-input
                  icon="icon icon-list"
                  type="number"
                  @change="(e) => (form.quantity = mathRound(e.target.value || 0, 0))"
                  icon-pack="feather"
                  class="w-full"
                  label="Quantity"
                  :value="mathRound(form.quantity || 0, 3)"
                  name="Quantity"
                  v-validate="'required'"
                />
                <span class="text-danger text-sm" v-show="errors.has('Quantity')">{{ errors.first('Quantity') }}</span>
              </vx-tooltip>
            </div>
          </template>
          <template v-else>
            <div class="vx-row mb-2" v-if="!isCustomCategory">
              <label for="">Weight Per Package <span class="text-danger">*</span></label>
              <vs-input
                icon="icon icon-list"
                step="0.001"
                type="number"
                icon-pack="feather"
                :value="mathRound(form.weight_per_package || 0, 3)"
                @change="(e) => (form.weight_per_package = mathRound(e.target.value || 0, 3))"
                class="w-full"
                name="Weight Per Package"
                v-validate=""
                disabled
              />
              <span class="text-danger text-sm" v-show="errors.has('Weight Per Package')">{{ errors.first('Weight Per Package') }}</span>
            </div>
            <div class="vx-row mb-2">
              <vx-tooltip class="w-full" position="left" text="To edit the quantity of this product, please use the update quantity feature under action in the product list.">
                <vs-input
                  icon="icon icon-list"
                  @change="(e) => (form.quantity = mathRound(e.target.value || 0, 0))"
                  type="number"
                  icon-pack="feather"
                  class="w-full"
                  label="Quantity"
                  v-model="form.quantity"
                  name="Quantity"
                  v-validate="'required'"
                />
                <span class="text-danger text-sm" v-show="errors.has('Quantity')">{{ errors.first('Quantity') }}</span>
              </vx-tooltip>
            </div>
          </template>

          <template v-if="![categories.ANCILLARY, categories.OTHER].includes(form.categoryId) && !isCustomCategory">
            <!-- batch code -->
            <div class="vx-row mb-2">
              <div class="block">
                <label for="">Batch <span class="text-danger">*</span></label>
                <vs-input icon="icon icon-edit" v-model="form.batchNumber" icon-pack="feather" class="w-full" name="Batch" v-validate="'required'" />
                <span class="text-danger text-sm" v-show="errors.has('Batch')">{{ errors.first('Batch') }}</span>
              </div>
              <div class="mt-4 ml-6 flex flex-wrap items-center justify-start">
                <vs-button class="mr-2" color="primary" type="relief" @click="showBatchPlantLookUp">Look Up</vs-button>
                <vs-button color="primary" type="relief" @click="generateBatchNumber">Generate</vs-button>
              </div>
            </div>

            <div class="vx-row mb-2" v-if="form.categoryId === categories.PLANT || form.categoryId === categories.MATURE">
              <vs-input
                :disabled="true"
                icon="icon icon-list"
                icon-pack="feather"
                class="w-full"
                label="Serial Number*"
                v-model="batchPlantsSerialNumber"
                @change="changeBatchPlantHandler"
                name="Serial Number"
              />
            </div>
          </template>

          <!-- add to market place -->
          <div class="vx-row mb-2 mt-5" v-if="isGrowersAndProcessors">
            <vs-checkbox class="mt-4" color="primary" v-model="form.show_in_marketplace">Add to Marketplace?</vs-checkbox>
          </div>
        </div>
      </div>

      <!-- form - vertical tabs -->
      <div class="mt-10">
        <vs-tabs ref="tabs" v-model="activeTab" position="left">
          <!-- more information -->
          <vs-tab v-if="!isCustomCategory" label="More Information">
            <more-information-tab :form.sync="form" :show-all="!isAncillaryAndOthers" />
          </vs-tab>

          <!-- promotional discount price -->
          <!-- <vs-tab label="Promotional Discount Price">
            <promotional-discount-price-tab :form.sync="form" />
          </vs-tab> -->

          <!-- media image -->
          <vs-tab label="Media (image)">
            <media-tab :form.sync="form" />
          </vs-tab>

          <!-- description -->
          <vs-tab v-if="!isCustomCategory && isGrowersAndProcessors" label="Description">
            <div class="tab_content tab__description">
              <div class="description__note vx-row items-center mx-2">
                <h3 class="mr-2">Description</h3>
              </div>

              <!-- textarea -->
              <vs-textarea class="mt-5" v-model="form.description" width="50%" height="150px" placeholder="Please enter your product's decription" />
            </div>
          </vs-tab>

          <!-- inventory -->
          <vs-tab v-if="!isCustomCategory && ![categories.ANCILLARY, categories.OTHER].includes(form.categoryId)" label="Inventory">
            <inventory-tab ref="inventory-tab" :form.sync="form" />
          </vs-tab>
        </vs-tabs>
      </div>

      <!-- Save & Reset Button -->
      <div class="vx-row">
        <div class="vx-col w-full">
          <div class="mt-8" vs-align="center" vs-type="flex" vs-justify="center">
            <vs-button class="mr-2 vs-con-loading__container" id="edit-product-button" @click="saveProduct()" :disabled="loading" type="relief">Save Product</vs-button>
            <vs-button to="/products/list" color="danger" type="relief" class="text-left" :disabled="loading">Cancel</vs-button>
          </div>
        </div>
      </div>
    </vs-card>

    <!-- batch model -->
    <batch-modal @added="selectBatchHandler" :active.sync="isBatchPlantModalShow" :form.sync="form" />
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import { weightTypes, weightMixins } from '@/utils/weightType'
import Select2 from '@/components/custom/form-elements/Select2.vue'
import InventoryTab from '@/views/products/components/InventoryTab.vue'
import MoreInformationTab from '@/views/products/components/MoreInformationTab.vue'
import PromotionalDiscountPriceTab from '@/views/products/components/PromotionalDiscountPriceTab.vue'
import MediaTab from '@/views/products/components/MediaTab.vue'
import BatchModal from '@/views/products/components/BatchModal.vue'
import { DEMORETAILER } from '@/utils/roles.js'

export default {
  name: 'EditProduct',
  components: {
    Select2,
    InventoryTab,
    MoreInformationTab,
    PromotionalDiscountPriceTab,
    MediaTab,
    BatchModal
  },
  data() {
    const emptyIventory = {
      inventoryId: null,
      weightType: null,
      inventory_quantity: 0,
      inventory_avail_quantity: 0
    }

    return {
      processing: false,
      emptyIventory,
      DEMORETAILER,
      isActiveBatchModal: false,
      activeTab: 0,
      weightTypeDisabled: false,
      strainOptions: [],
      tieredPricingOptions: [],
      form: {
        categoryId: null,
        tieredPricingGroupId: null,
        weight_type: null,
        supplierId: null,
        supplier_type: null,
        batchNumber: '',
        product_name: '',
        show_in_marketplace: false,
        description: '',
        sales_price: 0,
        weight_per_package: 0,
        batchPlants: [],
        cHoldBatchPlants: [],
        room_type: 'Indoor',
        plant_stage: 'seedling',
        grow_method: 'Soil',
        purchase_price: 0,
        profit_margin: 0,
        waste_in_pounds: 0,
        alert_quantity: 0,
        quantity: 0,
        thc: 0,
        cbd: 0,
        terpenoid_potency: 0,
        inventories: [],
        discounts: [],
        product_images: []
      },
      weightTypeOptions: weightTypes,

      // batch plant modal mounted
      isBatchPlantModalMounted: false,
      isBatchPlantModalShow: false
    }
  },

  mixins: [weightMixins],

  async mounted() {
    this.processing = true
    this.$validator.pause()

    try {
      const { data } = await this.getProductById(this.$route.params.id)

      this.form = Object.assign({ batchPlants: [], batchNumber: data.product.batch && data.product.batch.batch_number }, data.product)
      this.form.product_images = (data.product.images || []).map((img) => ({
        name: img.image_name,
        file_name: img.image_name,
        url: img.image_path,
        id: img.id,
        manually: true
      }))
      this.form.discounts = data.product.product_discount.map((d, i) => ({
        ...d,
        localId: i
      }))
      let invs = data.product.product_inventories
      if (!data.product.product_inventories || !data.product.product_inventories.length) {
        invs = [this.emptyIventory]
      }
      this.form.inventories = invs.map((i) => ({
        id: i.id,
        inventoryId: i.inventoryId,
        weightType: i.weight_type,
        inventory_quantity: i.quantity,
        inventory_avail_quantity: i.quantity
      }))

      setTimeout(() => {
        this.form.batchPlants = (data.product.product_plants || []).reduce((coll, item) => {
          if (item && item.plant && item.status) {
            coll.push({
              plantId: item.plantId,
              serialNumber: item.plant.serialNumber
            })
          }
          return coll
        }, [])
      }, 0)

      this.$nextTick(() => {
        this.$validator.reset()
        this.$validator.resume()
      })

      this.processing = false
    } catch ({ message }) {
      this.processing = false
      this.$vs.notify({
        title: 'Error',
        text: message,
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger'
      })
    }

    // remove unit weight type
    this.weightTypeOptions = weightTypes.reduce((acc, item) => {
      if (item.value !== 'unit') {
        acc.push(item)
      }

      return acc
    }, [])

    // get tiered pricing
    await this.getTieredPricingOptions()
  },
  computed: {
    ...mapState('product', ['loading', 'categories', 'product']),
    ...mapState('permission', ['roles']),
    ...mapState('auth', ['user', 'growerAndProcessorRoleGroup']),

    // extraa option for supplier
    extraSupplierOptions() {
      return [{ label: 'My Business', value: this.user && this.user.id, type: 'user' }]
    },

    validateForm() {
      //check all the fields and ingedients details to
      return !this.errors.any() && !(this.$refs['ingredient'] && this.$refs['ingredient'].map((child) => child.errors.any()).find((bool) => bool))
    },
    growersAndProcessors() {
      return this.growerAndProcessorRoleGroup
    },
    isGrowersAndProcessors() {
      return this.growersAndProcessors.includes(this.user.role.id)
    },
    isAncillaryAndOthers() {
      const { ANCILLARY, OTHER } = this.categories
      return [ANCILLARY, OTHER].includes(this.form.categoryId)
    },

    // custome category
    isCustomCategory() {
      return this.user.roleId === DEMORETAILER
    },

    batchPlantsSerialNumber: {
      get() {
        return this.form.batchPlants.map((x) => x.serialNumber).join(',')
      },

      set(val) {
        if (val) {
          const tempSerials = val.split(',')

          this.form.batchPlants = tempSerials.reduce((acc, item) => {
            const temPlant = this.form.cHoldBatchPlants.find((x) => x.serialNumber === item)
            if (temPlant) {
              acc.push(temPlant)
            } else {
              acc.push({
                plantId: null,
                serialNumber: item
              })
            }

            return acc
          }, [])
        } else {
          this.form.batchPlants = []
        }
      }
    }
  },
  methods: {
    ...mapActions('product', ['getProductById', 'updateProduct', 'updateProductPlants']),
    ...mapMutations('product', { setState: 'SET_STATE' }),

    // get tiered pricing options
    async getTieredPricingOptions() {
      this.$store
        .dispatch('setting/getTieredPricingList', { page: 1, limit: 0 })
        .then((res) => {
          this.tieredPricingOptions = res.data.reduce((acc, item) => {
            const tempObj = {}
            tempObj.label = item.pricing_group_name
            tempObj.value = item.id

            acc.push(tempObj)

            return acc
          }, [])
        })
        .catch((err) => {
          const { message } = err
          this.$vs.notify({
            title: 'Error',
            text: message,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
        })
    },

    generateBatchNumber() {
      this.form.batchNumber = `BN${new Date().getTime()}`
    },

    calculateSalePrice() {
      this.form.sales_price = this.mathRound(this.form.sales_price || 0, 2)
      if (this.form.sales_price > 0 && this.form.purchase_price > 0 && this.form.purchase_price <= this.form.sales_price) {
        this.form.profit_margin = this.mathRound(((this.form.sales_price - this.form.purchase_price) / this.form.purchase_price) * 100, 2)
      } else {
        this.form.profit_margin = 0
      }
    },

    async saveProduct() {
      //validate inventory details
      const check1 = await this.$validator.validate()

      if (!check1) {
        this.$vs.notify({
          title: 'Error',
          text: 'please fill the form first',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
        return
      }
      if (this.$refs['inventory-tab']) {
        //validate all ingredient details to
        const check2 = (await Promise.all(this.$refs['inventory-tab'].$refs['inventory'].map((child) => child.$validator.validate()))).filter((bool) => !bool).length === 0
        if (!check2) {
          this.$vs.notify({
            title: 'Error',
            text: 'please fill the form first',
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })
          return
        }
      }

      const isValid = this.form.inventories.find((i) => {
        if (i.inventoryId) {
          if (!(i.inventory_quantity > 0 && i.inventory_avail_quantity > 0 && i.inventory_avail_quantity >= i.inventory_quantity)) {
            return true
          }
        }
      })

      if (isValid) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please Check Inventory',
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
        return
      }

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Are you sure?',
        text: 'Are you sure you want to save this record?',
        acceptText: 'Yes, I am sure!',
        cancelText: 'No, cancel it!',
        accept: await this.editSaveProduct
      })
    },

    // update product
    async editSaveProduct() {
      try {
        const payload = {
          ...this.form,
          inventories: this.form.inventories.filter((i) => !!i.inventoryId),
          batchPlantIds: this.form.batchPlants,
          product_images: this.form.product_images.map((i) => i.file_name)
        }

        if (this.user.roleId === DEMORETAILER) {
          payload.supplierId = this.user.id
          payload.supplier_type = 'user'
          payload.weight_type = 'unit'
        }

        // delete duplicate entry
        delete payload.batchPlants
        delete payload.product_plants
        delete payload.batch

        const { message } = await this.updateProduct({
          id: this.$route.params.id,
          data: payload
        })
        this.$router.push({ name: 'product-list' })
        this.$vs.notify({
          title: 'Success',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'success'
        })
      } catch ({ message }) {
        this.$vs.notify({
          title: 'Error',
          text: message,
          iconPack: 'feather',
          icon: 'icon-alert-circle',
          color: 'danger'
        })
      }
    },

    showBatchPlantLookUp() {
      this.isBatchPlantModalMounted = true
      this.isBatchPlantModalShow = true
    },

    selectBatchHandler(data) {
      this.form.batchNumber = data.batch_number
      this.$nextTick(() => {
        this.form.batchPlants = data.plants
        this.$nextTick(() => {
          this.updateProductPlants({
            id: this.$route.params.id,
            data: {
              batchNumber: this.form.batchNumber,
              batchPlantIds: this.form.batchPlants
            }
          })
        })
      })
    },

    changeBatchPlantHandler(event) {
      const val = event.target.value

      for (let index = 0; index < val.length; index++) {
        const element = val[index]
        if (val.filter((x) => x.serialNumber === element).length > 1) {
          this.$vs.notify({
            title: 'Error',
            text: `Serial no '${element}' already exist!`,
            iconPack: 'feather',
            icon: 'icon-alert-circle',
            color: 'danger'
          })

          break
        }
      }
    }
  },

  watch: {
    product() {
      if (this.product.supplier_type === 'user') {
        this.extraSupplierOptions[0].value = this.product.supplierId
      }
    },

    'form.batchPlants': {
      immediate: true,
      handler() {
        if (this.form.categoryId === this.categories.PLANT || this.form.categoryId === this.categories.MATURE) {
          this.form.quantity = this.form.batchPlants.length || 0
        }
      }
    },

    'form.batchNumber': {
      immediate: true,
      handler() {
        this.form.batchPlants = []
      }
    },

    'form.categoryId': {
      immediate: true,
      handler() {
        // this.$validator.pause()
        if (this.form.categoryId === this.categories.PLANT || this.form.categoryId === this.categories.MATURE) {
          this.form.weight_type = 'unit'
        } else {
          this.form.batchPlants = []
        }

        if (this.isAncillaryAndOthers) {
          this.weightTypeDisabled = true
          this.form.weight_type = 'unit'
          this.form.batchNumber = null
          if (this.activeTab === 4) {
            this.activeTab = 0
          }
          this.$refs.tabs.$refs.li[4].style.display = 'none'
        } else if (this.isGrowersAndProcessors) {
          this.weightTypeDisabled = false
          this.$refs.tabs.$refs.li[4].style.display = 'block'
        } else {
          this.weightTypeDisabled = false
          this.$refs.tabs.$refs.li[4].style.display = 'none'
        }

        this.$nextTick(() => {
          this.$validator.reset()
          this.$validator.resume()
        })
      }
    },

    processing() {
      if (this.processing) {
        this.$vs.loading({
          scale: 0.45,
          container: '#edit-product-container'
        })
      } else {
        this.$vs.loading.close('#edit-product-container > .con-vs-loading')
      }
    },

    loading() {
      if (this.loading) {
        this.$vs.loading({
          container: '#edit-product-button',
          scale: 0.45
        })
      } else {
        this.$vs.loading.close('#edit-product-button > .con-vs-loading')
      }
    },

    isBatchPlantModalShow: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          setTimeout(() => {
            this.isBatchPlantModalMounted = false
          }, 0)
        }
      }
    }
  },

  destroyed() {
    this.setState({
      action: 'product',
      data: null
    })
  }
}
</script>

<style lang="scss">
#page-add-product {
  .vs-tabs-position-left {
    .con-ul-tabs {
      .ul-tabs-left {
        .vs-tabs--li {
          button {
            text-align: left !important;
          }
        }
      }
    }

    // tab content
    .tab_content {
      border: 1px solid rgba(0, 0, 0, 0.2);
      border-radius: 10px;
      overflow: hidden;
      padding: 15px;
    }

    // dropzone
    .con-input-upload {
      width: 98%;
    }
  }
}
</style>
